import { Collapsible } from 'radix-ui';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { useMobileMenu } from '@/hooks/useMobileMenu';
import { Logo } from '@/components/ui/Logo';
import { UI_STRINGS } from '@/lib/constants/ui-strings';

const NAV_LINKS = [
  { label: 'Home', href: ROUTES.HOME },
  { label: 'Benefits', href: ROUTES.BENEFITS },
  { label: 'Due Diligence Platform', href: ROUTES.DUE_DILIGENCE_PLATFORM },
  // Public Trusted Partners marketing page (MYN-216/MYN-215) - `/partners`,
  // not `ROUTES.TRUSTED_PARTNERS` (`/trusted-partners`), which is the
  // unrelated authenticated in-app Perks page (A-126).
  { label: 'Trusted Partners', href: ROUTES.PARTNERS },
  { label: 'Investors', href: ROUTES.INVESTORS },
] as const;

export interface PublicHeaderProps {
  /**
   * `minimal` drops the "Home" link and the Sign In / Get Started CTAs,
   * leaving only the marketing nav links - the header the public
   * shared-profile screens (MYN-58) use, where an external recipient has no
   * account to sign into. `full` (default) is the marketing-site header.
   */
  variant?: 'full' | 'minimal';
}

export function PublicHeader({ variant = 'full' }: PublicHeaderProps) {
  const minimal = variant === 'minimal';
  const navLinks = minimal ? NAV_LINKS.filter((link) => link.label !== 'Home') : NAV_LINKS;
  const { open, setOpen, containerRef } = useMobileMenu();
  const location = useLocation();

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} ref={containerRef} className="relative">
      {/* `h-public-header` (= the old `h-20`, 5rem) rather than a bare `h-20`,
        so `SharedProfileLayout` can publish this exact height as its
        `--app-chrome-offset` and the two cannot drift - same arrangement
        `AppNavbar`/`AppLayout` already use. */}
      <header className="h-public-header bg-white border-b border-[rgba(0,25,40,0.06)] flex items-center justify-center">
        {/* `max-w-328 px-4` matches the public-page section renderers' own
          container exactly (MYN-211/FR-127) - the previous bespoke
          `clamp(16px, 5vw, 304px)` padding grew to a 304px left gutter on
          wide screens, reading as "not left-aligned" against the page
          content below it. */}
        <div className="w-full h-full max-w-328 mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link
            to={ROUTES.HOME}
            className="shrink-0"
            aria-label={`${UI_STRINGS.BRAND_NAME} home`}
          >
            <Logo variant="color" height={36} aria-hidden />
          </Link>

          {/* Desktop nav + CTA grouped together (MYN-211/FR-128: nav sits
            adjacent to Login, not spread apart by `justify-between`). */}
          <div className="hidden md:flex items-center gap-6">
            <nav className="flex items-center gap-1" aria-label="Public navigation">
              {navLinks.map(({ label, href }) => {
                const isActive = location.pathname === href;
                return (
                  <a
                    key={label}
                    href={href}
                    aria-current={isActive ? 'page' : undefined}
                    className={`text-base font-medium px-4 py-2.5 rounded-full underline-offset-4 transition-colors ${
                      isActive
                        ? 'text-dkgreen underline'
                        : 'text-navy no-underline hover:bg-[rgba(0,25,40,0.05)]'
                    }`}
                  >
                    {label}
                  </a>
                );
              })}
            </nav>

            {/* Dropped on the minimal (shared-profile) header */}
            {!minimal && (
              <div className="flex items-center gap-[10px]">
                <Link
                  to={ROUTES.AUTH.LOGIN}
                  className="text-navy text-base font-medium px-4 py-2.5"
                >
                  {UI_STRINGS.NAV.SIGN_IN}
                </Link>
                <Link
                  to={ROUTES.AUTH.REGISTER}
                  className="bg-dkgreen text-white text-sm font-medium px-8 py-4 rounded-button hover:bg-dkgreen-hover transition-colors"
                >
                  {UI_STRINGS.NAV.GET_STARTED}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <Collapsible.Trigger asChild>
            <button
              type="button"
              className="md:hidden p-2 text-navy rounded-lg hover:bg-gray-50 transition-colors"
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              {open ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          </Collapsible.Trigger>
        </div>
      </header>

      {/* Mobile menu */}
      <Collapsible.Content>
        <nav
          className="absolute top-full left-0 right-0 bg-white border-b border-gray-100 md:hidden z-50 shadow-md"
          aria-label="Mobile public navigation"
        >
          <div className="flex flex-col p-4 gap-1">
            {navLinks.map(({ label, href }) => {
              const isActive = location.pathname === href;
              return (
                <a
                  key={label}
                  href={href}
                  onClick={() => setOpen(false)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`px-4 py-3 text-sm font-medium rounded-lg underline-offset-4 transition-colors hover:bg-gray-50 ${
                    isActive ? 'text-dkgreen underline' : 'text-navy no-underline hover:text-dkgreen'
                  }`}
                >
                  {label}
                </a>
              );
            })}
            {!minimal && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-2">
                <Link
                  to={ROUTES.AUTH.LOGIN}
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-navy text-center"
                >
                  {UI_STRINGS.NAV.SIGN_IN}
                </Link>
                <Link
                  to={ROUTES.AUTH.REGISTER}
                  onClick={() => setOpen(false)}
                  className="bg-dkgreen text-white text-sm font-medium px-5 py-2 rounded-button text-center hover:bg-dkgreen-hover transition-colors"
                >
                  {UI_STRINGS.NAV.GET_STARTED}
                </Link>
              </div>
            )}
          </div>
        </nav>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
